import { z } from "zod";
import { zEmail, zId } from "./common.js";
import { zPermission, zSystemRoleKey } from "./permissions.js";
import { zCurrencyCode } from "./currency.js";
import { zLanguage } from "./i18n.js";

/** org feature flags — international giving stays invisible unless enabled */
export const zOrgSettings = z.object({
  multiCurrency: z.boolean().default(false),
  giftAid: z.boolean().default(false),
  /** flipped by the server when SMS provisioning succeeds */
  sms: z.boolean().default(false),
  /** org voice for AI drafting — tone notes every draft prompt carries */
  aiVoice: z.string().max(600).optional(),
  /** receipts for split gifts list each fund under the single total (CT-RM-18) */
  receiptFundBreakdown: z.boolean().optional(),
  /** CT-RM-30: language base content is written in (see orgLanguages) */
  defaultLanguage: zLanguage.optional(),
  /** CT-RM-30: languages offered on public pages */
  languages: z.array(zLanguage).optional(),
  /** CT-RM-33: SCIM-created users go through the invite flow (status invited) instead of starting active */
  scimSendInvites: z.boolean().optional(),
});
export type OrgSettings = z.infer<typeof zOrgSettings>;

export const zOrganization = z.object({
  id: zId,
  name: z.string(),
  slug: z.string(),
  timezone: z.string().default("America/New_York"),
  fiscalYearStartMonth: z.number().int().min(1).max(12).default(1),
  currency: z.string().length(3).default("USD"),
  logo: z.string().nullable().default(null),
  settings: zOrgSettings.partial().passthrough().default({}),
  createdAt: z.string(),
});
export type Organization = z.infer<typeof zOrganization>;

export const zUpdateOrganization = z.object({
  name: z.string().trim().min(2).max(200).optional(),
  timezone: z.string().optional(),
  fiscalYearStartMonth: z.number().int().min(1).max(12).optional(),
  currency: zCurrencyCode.optional(),
  settings: zOrgSettings.optional(),
});

export const zOrgUser = z.object({
  id: zId,
  email: zEmail,
  name: z.string(),
  roleId: zId,
  roleName: z.string(),
  status: z.enum(["active", "invited", "suspended"]),
  /** CT-RM-33: provisioned by the org's identity provider; name, email and status are IdP-owned */
  scimManaged: z.boolean(),
  lastLoginAt: z.string().nullable(),
  createdAt: z.string(),
});
export type OrgUser = z.infer<typeof zOrgUser>;

export const zInviteUserRequest = z.object({
  email: zEmail,
  name: z.string().trim().min(1).max(200),
  roleId: zId,
});

export const zRole = z.object({
  id: zId,
  key: z.string().nullable().describe("system role key, null for custom roles"),
  name: z.string(),
  description: z.string().nullable(),
  permissions: z.array(zPermission),
  isSystem: z.boolean(),
});
export type Role = z.infer<typeof zRole>;

export const zCreateRoleRequest = z.object({
  name: z.string().trim().min(2).max(100),
  description: z.string().max(500).optional(),
  permissions: z.array(zPermission).min(1),
});

export const zUpdateRoleRequest = zCreateRoleRequest.partial();

export const zUpdateUserRequest = z.object({
  roleId: zId.optional(),
  status: z.enum(["active", "suspended"]).optional(),
  name: z.string().trim().min(1).max(200).optional(),
});

export { zSystemRoleKey };
